// Add Obligation wizard — "Ask AI" obligation-creation coach modal. Cloned
// from the Record Payment wizard's RecordPaymentExplainModal: fires the
// initial explanation automatically on open (question null, no history),
// shows a thinking line while pending, keeps a running staff-question /
// coach-answer transcript, and lets the user ask follow-ups (prior turns
// ride the request as `history`). The wizard's current selections snapshot
// rides every request; all state is local and closing discards it.

import { useEffect, useState } from "react";
import { MessageCircleQuestion } from "lucide-react";
import { Button, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useExplainObligationCreation } from "@/hooks/portfolioAdmin/useLoanFees";
import type {
  ObligationCreationExplainContext,
  ObligationExplainTurn,
} from "@/api/portfolioAdmin/loanFeesApi";

export interface ObligationCreationExplainModalProps {
  loanId: string;
  /** Snapshot of the wizard's in-progress choices at open time. */
  context: ObligationCreationExplainContext;
  onClose: () => void;
}

export function ObligationCreationExplainModal({
  loanId,
  context,
  onClose,
}: ObligationCreationExplainModalProps) {
  const explain = useExplainObligationCreation();
  const [turns, setTurns] = useState<ObligationExplainTurn[]>([]);
  const [question, setQuestion] = useState("");
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const ask = (q: string | null) => {
    // History = the transcript BEFORE this question; the question itself
    // rides separately. The server caps history at 12 turns — keep the most
    // recent turns so a long conversation degrades gracefully instead of
    // 400ing.
    const recent = turns.slice(-12);
    const history = recent.length > 0 ? recent : null;
    if (q) {
      setTurns((cur) => [...cur, { role: "user", content: q }]);
      setQuestion("");
    }
    setErrorMessage(null);
    setPending(true);
    explain.mutate(
      { loanId, body: { question: q, history, context } },
      {
        onSuccess: (res) =>
          setTurns((cur) => [...cur, { role: "assistant", content: res.answer }]),
        onError: (err) =>
          setErrorMessage(
            err instanceof Error && err.message
              ? err.message
              : "The coach couldn't reply. Try again.",
          ),
        onSettled: () => setPending(false),
      },
    );
  };

  // Auto-fire the initial explanation once on mount (the wizard mounts this
  // modal only while it's open).
  useEffect(() => {
    ask(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trimmed = question.trim();

  return (
    <Modal
      open
      onClose={onClose}
      title="Ask AI about this fee"
      description="Explains what creating this fee or obligation will do — grounded in this loan's waterfall."
      size="lg"
      dismissOnBackdropClick={false}
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="obligation-explain-modal">
        <ol className="m-0 p-0 list-none flex flex-col gap-space-3">
          {turns.map((t, i) => (
            <li key={i} data-testid={`explain-turn-${t.role}`}>
              <p className="text-caption uppercase tracking-wide text-gray-mid m-0 mb-space-1">
                {t.role === "user" ? "You asked" : "Coach"}
              </p>
              <div
                className={
                  "rounded-md border border-border-light p-space-3 text-body whitespace-pre-wrap " +
                  (t.role === "user" ? "bg-white-warm" : "bg-white-pure")
                }
              >
                {t.content}
              </div>
            </li>
          ))}
        </ol>

        {pending && (
          <p
            className="text-body text-gray-mid italic m-0"
            data-testid="explain-thinking"
          >
            Reviewing this loan's waterfall…
          </p>
        )}

        {errorMessage && (
          <p role="alert" className="text-caption text-status-error m-0">
            {errorMessage}
          </p>
        )}

        <div className="flex flex-col gap-space-1">
          <Textarea
            rows={2}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask a follow-up — e.g. Who ends up with this money?"
            aria-label="Follow-up question"
            disabled={pending}
          />
          <div className="flex justify-end">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!trimmed || pending}
              onClick={() => ask(trimmed)}
              data-testid="explain-ask-button"
            >
              <MessageCircleQuestion size={14} aria-hidden /> Ask
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
